import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  BuildingAssigneeReadModel,
  CompanyReadModel,
  PermissionKeys,
  useGetSpacesStakeholdersQuery,
} from '@client/api';
import { Button, Card, CardActions, CardContent, CardHeader, CardTitle, Pagination, Skeleton } from '@client/toolkit';

import { SelectionModal } from '../../../../components/selection-modal/selection-modal';
import { useSpacePermissions } from '../../../../hooks/useSpacePermissions/useSpacePermissions';
import { CompaniesTable } from './companies-table';
import { useSpace } from '../../context/space-context';
import { ChangeManagerOwnerDialog } from './change-manager-owner-dialog';

type Role = 'Owner' | 'Manager' | 'Investor';

export interface CompaniesCardProps {
  className?: string;
  title?: string;
  role: Role;
  companies: BuildingAssigneeReadModel[];
  buildingId?: string;
  spaceId?: string;
  isLoading: boolean;
  onEdit?: (data: CompanyReadModel[]) => void;
}

export function CompaniesCard({
  className,
  title,
  companies,
  isLoading = false,
  spaceId: parentSpaceId,
  buildingId: parentBuildingId,
  role,
  onEdit,
}: CompaniesCardProps) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { space } = useSpace();
  const { hasAnyPermission } = useSpacePermissions({ id: parentSpaceId ?? parentBuildingId });
  const canEdit = hasAnyPermission([PermissionKeys.Space.Update]);

  const pageSize = 25;
  const [page, setPage] = useState(0);
  const [selectedCompanies, setSelectedCompanies] = useState<CompanyReadModel[]>([]);
  const [openCompanyEditModal, setOpenCompanyEditModal] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);

  const handleEdit = (value: CompanyReadModel[]) => {
    setSelectedCompanies(value);
    setOpenConfirmationModal(true);
  };

  const onConfirmEdit = () => {
    onEdit?.(selectedCompanies);
    setOpenConfirmationModal(false);
    setOpenCompanyEditModal(false);
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex-1" variant="dense">
          {title}
        </CardTitle>
        {/* TODO: Re-enable Rediger button for Manager when ready */}
        {canEdit && role !== 'Manager' ? (
          <CardActions>
            <Button color="primary-dark" size="sm" disabled={isLoading} onClick={() => setOpenCompanyEditModal(true)}>
              {tCommon('edit')}
            </Button>
          </CardActions>
        ) : null}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-full w-full" />
        ) : (
          <Card>
            <CompaniesTable selectedCompanies={companies} isLoading={isLoading} role={role} />
            <Pagination totalCount={companies.length} currentPage={page} setPage={setPage} pageSize={pageSize} />
          </Card>
        )}
      </CardContent>
      <SelectionModal
        open={openCompanyEditModal}
        selected={companies as CompanyReadModel[]}
        onClose={() => setOpenCompanyEditModal(false)}
        onSubmit={handleEdit}
        title={tCommon('selectEntity', {
          entity: tGlobal(`entity.${role.toLowerCase()}`, { context: 'plural' }).toLowerCase(),
        })}
        searchQuery={useGetSpacesStakeholdersQuery}
        getItemName={(building) => building.name ?? ''}
        entityName={`entity.${role.toLowerCase()}`}
        maxSelectedItems={role === 'Manager' ? 1 : undefined}
      />
      <ChangeManagerOwnerDialog
        open={openConfirmationModal}
        onClose={() => setOpenConfirmationModal(false)}
        onConfirm={onConfirmEdit}
        hasActiveAgreements={space?.hasActiveAgreements ?? false}
      />
    </Card>
  );
}
